import Papa from 'papaparse';
import * as pdfjsLib from 'pdfjs-dist';


// Configure PDF.js worker - use bundled worker instead of CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
)?.toString();

export const csvImportService = {
  /**
   * Process file (CSV or PDF) with validation
   */
  async processFile(file) {
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file?.size > maxSize) {
      throw new Error('File size exceeds 10MB limit');
    }

    // Validate file type
    const fileType = file?.type || '';
    const fileName = file?.name || '';
    
    if (fileName?.toLowerCase()?.endsWith('.csv') || fileType?.includes('csv')) {
      return await this.parseCSV(file);
    } else if (fileName?.toLowerCase()?.endsWith('.pdf') || fileType?.includes('pdf')) {
      return await this.parsePDF(file);
    } else {
      throw new Error('Unsupported file format. Please upload CSV or PDF files only.');
    }
  },

  /**
   * Parse CSV file with automatic header detection
   */
  async parseCSV(file) {
    return new Promise((resolve, reject) => {
      // First pass - detect structure
      Papa.parse(file, {
        preview: 10, // Read first 10 rows to detect headers
        skipEmptyLines: true,
        complete: (results) => {
          const { data } = results;
          
          if (!data || data.length === 0) {
            reject(new Error('File is empty or could not be read'));
            return;
          }
          
          // Detect if file has headers
          const hasHeaders = this.detectHeaders(data);
          
          // Second pass - parse with correct settings
          Papa.parse(file, {
            header: hasHeaders,
            skipEmptyLines: true,
            dynamicTyping: false, // Keep everything as strings for now
            complete: (finalResults) => {
              if (hasHeaders) {
                resolve({
                  hasHeaders: true,
                  headers: finalResults.meta.fields || [],
                  data: finalResults.data || [],
                  rawData: data
                });
              } else {
                resolve({
                  hasHeaders: false,
                  headers: [],
                  data: finalResults.data || [],
                  rawData: data
                });
              }
            },
            error: (error) => {
              reject(new Error(`CSV parsing error: ${error.message}`));
            }
          });
        },
        error: (error) => {
          reject(new Error(`CSV reading error: ${error.message}`));
        }
      });
    });
  },

  /**
   * Detect if first row contains headers
   */
  detectHeaders(data) {
    if (!data || data?.length < 2) return false;
    
    const firstRow = data?.[0];
    const secondRow = data?.[1];
    
    if (!firstRow || !secondRow) return false;
    
    // Check if first row has text and second row has numbers/dates
    const firstRowHasText = firstRow?.some(cell => 
      cell && isNaN(parseFloat(cell)) && String(cell)?.trim()?.length > 0
    );
    
    const secondRowHasNumbers = secondRow?.some(cell => 
      cell && (!isNaN(parseFloat(cell)) || this.isDateString(cell))
    );
    
    return firstRowHasText && secondRowHasNumbers;
  },

  /**
   * Check if string is a date
   */
  isDateString(str) {
    if (!str) return false;
    const date = new Date(str);
    return date instanceof Date && !isNaN(date?.getTime());
  },

  /**
   * Extract text from PDF file with improved parsing
   */
  async parsePDF(file) {
    try {
      const arrayBuffer = await file?.arrayBuffer();
      const pdf = await pdfjsLib?.getDocument({ data: arrayBuffer })?.promise;
      
      let fullText = '';
      const lines = [];
      
      // Extract text from all pages
      for (let i = 1; i <= pdf?.numPages; i++) {
        const page = await pdf?.getPage(i);
        const textContent = await page?.getTextContent();
        
        // Group text items by Y position (same line)
        const lineMap = new Map();
        textContent?.items?.forEach(item => {
          const y = Math.round(item?.transform?.[5]);
          const x = Math.round(item?.transform?.[4]);
          if (!lineMap?.has(y)) {
            lineMap?.set(y, []);
          }
          lineMap?.get(y)?.push({ text: item?.str, x });
        });
        
        // Sort by Y position (top to bottom) and X position (left to right)
        const sortedLines = Array.from(lineMap?.entries())
          ?.sort((a, b) => b?.[0] - a?.[0])
          ?.map(([_, items]) => {
            // Sort items by X position within the same line
            const sortedItems = items?.sort((a, b) => a?.x - b?.x);
            return sortedItems?.map(item => item?.text)?.join(' ')?.trim();
          })
          ?.filter(line => line?.length > 0);
        
        lines?.push(...sortedLines);
        fullText += sortedLines?.join('\n') + '\n';
      }
      
      // Intelligently extract transactions from PDF
      const transactions = this.extractTransactionsFromPDF(lines, fullText);
      
      if (transactions?.length === 0) {
        throw new Error('No transaction data found in PDF. Please ensure the PDF contains a readable transaction table with dates, descriptions, and amounts.');
      }
      
      // Return in a format that bypasses column mapping
      return {
        isPDF: true,
        hasHeaders: false,
        headers: [],
        data: transactions,
        rawText: fullText,
        transactionCount: transactions?.length
      };
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error(`Failed to parse PDF file: ${error?.message}`);
    }
  },

  /**
   * Intelligently extract transactions from PDF text
   */
  extractTransactionsFromPDF(lines, fullText) {
    const transactions = [];
    
    // Enhanced date patterns with more formats
    const datePatterns = [
      /\b(\d{1,2})[\.\/\-](\d{1,2})[\.\/\-](\d{2,4})\b/g, // MM/DD/YYYY or DD/MM/YYYY or MM.DD.YYYY
      /\b(\d{4})[\.\/\-](\d{1,2})[\.\/\-](\d{1,2})\b/g, // YYYY-MM-DD
      /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{1,2}),?\s+(\d{4})\b/gi, // Month DD, YYYY
      /\b(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+(\d{4})\b/gi, // DD Month YYYY
      /\b(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\b/gi // DD Month (current year)
    ];
    
    // Enhanced amount pattern with more currency symbols and formats
    const amountPattern = /([\$£€¥₹]?\s*-?\s*\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?|\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?\s*[\$£€¥₹]?|\(\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?\))/g;
    
    // Try table-based extraction first (more structured)
    const tableTransactions = this.extractFromTableStructure(lines);
    if (tableTransactions?.length > 0) {
      return tableTransactions;
    }
    
    // Fallback to line-by-line extraction
    let currentTransaction = null;
    
    for (let i = 0; i < lines?.length; i++) {
      const line = lines?.[i];
      
      // Skip header lines and empty lines
      if (this.isHeaderLine(line) || line?.trim()?.length < 5) {
        continue;
      }
      
      // Try to find a date in the line
      let dateMatch = null;
      let dateStr = null;
      
      for (const pattern of datePatterns) {
        pattern.lastIndex = 0; // Reset regex
        const match = pattern?.exec(line);
        if (match) {
          dateMatch = match;
          dateStr = match?.[0];
          break;
        }
      }
      
      // If we found a date, this might be a new transaction
      if (dateStr) {
        // Save previous transaction if exists
        if (currentTransaction && currentTransaction?.amount > 0) {
          transactions?.push(currentTransaction);
        }
        
        // Extract amounts from the line
        const amounts = [];
        let amountMatch;
        const amountRegex = new RegExp(amountPattern);
        
        while ((amountMatch = amountRegex?.exec(line)) !== null) {
          const amountStr = amountMatch?.[0];
          const cleanAmount = this.parseAmount(amountStr);
          if (cleanAmount !== null && Math.abs(cleanAmount) > 0) {
            amounts?.push({ str: amountStr, value: cleanAmount, index: amountMatch?.index });
          }
        }
        
        // Extract description (text between date and first amount)
        const dateIndex = line?.indexOf(dateStr);
        let description = '';
        let transactionAmount = 0;
        
        if (amounts?.length > 0) {
          const firstAmountIndex = amounts?.[0]?.index;
          description = line?.substring(dateIndex + dateStr?.length, firstAmountIndex)?.trim();
          
          // Determine which amount to use
          // If multiple amounts, usually: [debit, credit, balance] or [amount, balance]
          if (amounts?.length >= 2) {
            // Use the second-to-last amount (before balance)
            transactionAmount = amounts?.[amounts?.length - 2]?.value;
          } else {
            transactionAmount = amounts?.[0]?.value;
          }
        } else {
          // No amount on this line, might be on next line
          description = line?.substring(dateIndex + dateStr?.length)?.trim();
        }
        
        // Clean up description
        description = description?.replace(/\s+/g, ' ')?.trim();
        
        // Start new transaction
        currentTransaction = {
          date: this.parseDate(dateStr),
          description: description || 'Transaction',
          amount: Math.abs(transactionAmount),
          type: this.determineTransactionType(description, transactionAmount),
          cleared: false,
          notes: ''
        };
        
      } else if (currentTransaction && !currentTransaction?.amount) {
        // This line might contain the amount for the previous transaction
        const amounts = [];
        let amountMatch;
        const amountRegex = new RegExp(amountPattern);
        
        while ((amountMatch = amountRegex?.exec(line)) !== null) {
          const amountStr = amountMatch?.[0];
          const cleanAmount = this.parseAmount(amountStr);
          if (cleanAmount !== null && Math.abs(cleanAmount) > 0) {
            amounts?.push({ str: amountStr, value: cleanAmount });
          }
        }
        
        if (amounts?.length > 0) {
          // Use the first significant amount found
          let transactionAmount = amounts?.length >= 2 ? amounts?.[amounts?.length - 2]?.value : amounts?.[0]?.value;
          currentTransaction.amount = Math.abs(transactionAmount);
          currentTransaction.type = this.determineTransactionType(currentTransaction?.description, transactionAmount);
        }
        
        // Append additional description text
        const cleanLine = line?.replace(amountPattern, '')?.trim();
        if (cleanLine?.length > 0 && !this.isHeaderLine(cleanLine)) {
          currentTransaction.description += ' ' + cleanLine;
          currentTransaction.description = currentTransaction?.description?.substring(0, 200)?.trim();
        }
      }
    }
    
    // Add last transaction
    if (currentTransaction && currentTransaction?.amount > 0) {
      transactions?.push(currentTransaction);
    }
    
    return transactions;
  },

  /**
   * Extract transactions from table structure
   */
  extractFromTableStructure(lines) {
    const transactions = [];
    let headerIndex = -1;
    let dateColIndex = -1;
    let descColIndex = -1;
    let amountColIndex = -1;
    
    // Find header row
    for (let i = 0; i < Math.min(20, lines?.length); i++) {
      const line = lines?.[i]?.toLowerCase();
      if (line?.includes('date') && (line?.includes('description') || line?.includes('merchant')) && line?.includes('amount')) {
        headerIndex = i;
        
        // Try to determine column positions
        const headerLine = lines?.[i];
        let dateMatch = /date/i?.exec(headerLine);
        const descMatch = /(description|merchant|details)/i?.exec(headerLine);
        let amountMatch = /amount/i?.exec(headerLine);
        
        if (dateMatch) dateColIndex = dateMatch?.index;
        if (descMatch) descColIndex = descMatch?.index;
        if (amountMatch) amountColIndex = amountMatch?.index;
        
        break;
      }
    }
    
    // If no clear header found, return empty (will use line-by-line extraction)
    if (headerIndex === -1) {
      return [];
    }
    
    // Process rows after header
    for (let i = headerIndex + 1; i < lines?.length; i++) {
      const line = lines?.[i];
      
      if (this.isHeaderLine(line) || line?.trim()?.length < 10) {
        continue;
      }
      
      // Try to extract date
      const datePattern = /\b(\d{1,2})[\.\/\-](\d{1,2})[\.\/\-](\d{2,4})\b/;
      let dateMatch = datePattern?.exec(line);
      
      if (!dateMatch) continue;
      
      let dateStr = dateMatch?.[0];
      
      // Extract amounts
      const amountPattern = /([\$£€¥₹]?\s*-?\s*\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?|\(\d{1,3}(?:[,\s]\d{3})*(?:\.\d{2})?\))/g;
      const amounts = [];
      let amountMatch;
      
      while ((amountMatch = amountPattern?.exec(line)) !== null) {
        const cleanAmount = this.parseAmount(amountMatch?.[0]);
        if (cleanAmount !== null && Math.abs(cleanAmount) > 0) {
          amounts?.push(cleanAmount);
        }
      }
      
      if (amounts?.length === 0) continue;
      
      // Extract description (between date and amount)
      const dateIndex = line?.indexOf(dateStr);
      const amountIndex = line?.lastIndexOf(amounts?.[amounts?.length - 1]?.toString());
      let description = line?.substring(dateIndex + dateStr?.length, amountIndex > dateIndex ? amountIndex : line?.length)?.trim();
      description = description?.replace(/[\$£€¥₹,]/g, '')?.replace(/\s+/g, ' ')?.trim();
      
      if (!description || description?.length < 2) {
        description = 'Transaction';
      }
      
      // Determine transaction amount (usually second-to-last before balance)
      let transactionAmount = amounts?.length >= 2 ? amounts?.[amounts?.length - 2] : amounts?.[0];
      
      transactions?.push({
        date: this.parseDate(dateStr),
        description: description?.substring(0, 200),
        amount: Math.abs(transactionAmount),
        type: this.determineTransactionType(description, transactionAmount),
        cleared: false,
        notes: ''
      });
    }
    
    return transactions;
  },

  /**
   * Determine transaction type based on description and amount
   */
  determineTransactionType(description, amount) {
    const descLower = (description || '')?.toLowerCase();
    
    // Income keywords
    const incomeKeywords = [
      'deposit', 'credit', 'payment received', 'salary', 'income', 'refund',
      'transfer in', 'interest earned', 'dividend', 'paycheck', 'reimbursement'
    ];
    
    // Check for income keywords
    const isIncome = incomeKeywords?.some(keyword => descLower?.includes(keyword));
    
    if (amount < 0) {
      return 'expense';
    } else if (isIncome) {
      return 'income';
    } else {
      // Default to expense for positive amounts without income keywords
      return 'expense';
    }
  },

  /**
   * Check if line is a header/title line
   */
  isHeaderLine(line) {
    const headerKeywords = [
      'date', 'description', 'amount', 'balance', 'transaction', 'debit', 'credit',
      'posted', 'merchant', 'category', 'account', 'statement', 'period',
      'beginning balance', 'ending balance', 'page', 'continued'
    ];
    
    const lineLower = line?.toLowerCase();
    
    // Check if line contains only header keywords (no actual transaction data)
    const hasOnlyKeywords = headerKeywords?.some(keyword => lineLower?.includes(keyword));
    const hasNoNumbers = !/\d/?.test(line);
    
    return hasOnlyKeywords && hasNoNumbers;
  },

  /**
   * Parse amount string to number
   */
  parseAmount(amountStr) {
    if (!amountStr) return null;
    
    // Remove currency symbols and spaces
    let cleaned = amountStr?.replace(/[\$£€¥\s]/g, '');
    
    // Handle parentheses as negative (accounting format)
    const isNegative = cleaned?.includes('(') || cleaned?.includes(')');
    cleaned = cleaned?.replace(/[()]/g, '');
    
    // Remove commas
    cleaned = cleaned?.replace(/,/g, '');
    
    // Check for negative sign
    const hasNegativeSign = cleaned?.startsWith('-');
    
    // Parse to float
    const value = parseFloat(cleaned);
    
    if (isNaN(value)) return null;
    
    return (isNegative || hasNegativeSign) ? -Math.abs(value) : value;
  },

  /**
   * Parse table structure from text lines (legacy method for fallback)
   */
  parseTableFromText(lines) {
    const tableData = [];
    
    for (const line of lines) {
      // Split by multiple spaces, tabs, or common delimiters
      const cells = line?.split(/\s{2,}|\t|\|/)?.map(cell => cell?.trim())?.filter(cell => cell?.length > 0);
      
      // Only include lines with at least 3 cells (date, description, amount)
      if (cells?.length >= 3) {
        tableData?.push(cells);
      }
    }
    
    return tableData;
  },

  /**
   * Auto-map CSV columns to transaction fields
   */
  autoMapColumns(headers) {
    if (!headers || headers?.length === 0) {
      return {
        date: null,
        description: null,
        amount: null,
        category: null
      };
    }
    
    const mapping = {
      date: null,
      description: null,
      amount: null,
      category: null
    };
    
    const lowerHeaders = headers?.map(h => String(h || '')?.toLowerCase());
    
    // Map date column
    const dateKeywords = ['date', 'transaction date', 'posted date', 'time', 'posted', 'trans date'];
    mapping.date = this.findColumnIndex(lowerHeaders, dateKeywords);
    
    // Map description column
    const descKeywords = ['description', 'merchant', 'payee', 'details', 'memo', 'name', 'transaction'];
    mapping.description = this.findColumnIndex(lowerHeaders, descKeywords);
    
    // Map amount column
    const amountKeywords = ['amount', 'value', 'total', 'debit', 'credit', 'transaction amount', 'sum'];
    mapping.amount = this.findColumnIndex(lowerHeaders, amountKeywords);
    
    // Map category column
    const categoryKeywords = ['category', 'type', 'classification', 'class'];
    mapping.category = this.findColumnIndex(lowerHeaders, categoryKeywords);
    
    return mapping;
  },

  /**
   * Find column index by keywords
   */
  findColumnIndex(headers, keywords) {
    for (let i = 0; i < headers?.length; i++) {
      const header = headers?.[i];
      if (keywords?.some(keyword => header?.includes(keyword))) {
        return i;
      }
    }
    return null;
  },

  /**
   * Map CSV row to transaction object with validation
   */
  mapRowToTransaction(row, columnMapping, hasHeaders, headers = []) {
    const getValue = (field) => {
      const index = columnMapping?.[field];
      if (index === null || index === undefined) return null;
      
      if (hasHeaders && headers?.length > 0) {
        // Row is an object with headers as keys
        // Use the header name directly from the headers array
        const headerKey = headers?.[index];
        return row?.[headerKey];
      } else if (hasHeaders) {
        // Fallback: try to get from object keys if headers not provided
        const objectKeys = Object.keys(row);
        const headerKey = objectKeys?.[index];
        return row?.[headerKey];
      } else {
        // Row is an array
        return row?.[index];
      }
    };
    
    let dateStr = getValue('date');
    let description = getValue('description');
    const amountStr = getValue('amount');
    const category = getValue('category');
    
    // Parse amount and determine type
    let amount = 0;
    if (amountStr) {
      // Remove currency symbols, commas, and other non-numeric characters except decimal and minus
      const cleanAmount = String(amountStr)?.replace(/[^0-9.-]/g, '');
      amount = parseFloat(cleanAmount) || 0;
    }
    
    let type = 'expense';
    
    if (amount < 0) {
      type = 'expense';
      amount = Math.abs(amount);
    } else if (amount > 0) {
      // Check if description suggests income
      const incomeKeywords = ['deposit', 'payment received', 'salary', 'income', 'refund', 'credit'];
      const descLower = String(description || '')?.toLowerCase();
      const isIncome = incomeKeywords?.some(keyword => descLower?.includes(keyword));
      type = isIncome ? 'income' : 'expense';
    }
    
    return {
      date: this.parseDate(dateStr),
      description: description?.trim() || 'Imported Transaction',
      amount: amount || 0,
      type,
      category: category?.trim() || null,
      cleared: false,
      notes: ''
    };
  },

  /**
   * Parse date string to YYYY-MM-DD format
   */
  parseDate(dateStr) {
    if (!dateStr) {
      const today = new Date();
      return today?.toISOString()?.split('T')?.[0];
    }
    
    try {
      const date = new Date(dateStr);
      if (isNaN(date?.getTime())) {
        const today = new Date();
        return today?.toISOString()?.split('T')?.[0];
      }
      return date?.toISOString()?.split('T')?.[0];
    } catch (error) {
      const today = new Date();
      return today?.toISOString()?.split('T')?.[0];
    }
  },

  /**
   * Validate transaction data quality
   */
  validateTransaction(transaction) {
    const issues = [];
    
    // Validate date
    if (!transaction?.date) {
      issues?.push({ field: 'date', message: 'Date is missing', severity: 'error' });
    } else {
      const date = new Date(transaction.date);
      if (isNaN(date?.getTime())) {
        issues?.push({ field: 'date', message: 'Invalid date format', severity: 'error' });
      } else {
        // Check if date is in the future
        const today = new Date();
        today?.setHours(0, 0, 0, 0);
        if (date > today) {
          issues?.push({ field: 'date', message: 'Date is in the future', severity: 'warning' });
        }
        
        // Check if date is too old (more than 10 years)
        const tenYearsAgo = new Date();
        tenYearsAgo?.setFullYear(tenYearsAgo?.getFullYear() - 10);
        if (date < tenYearsAgo) {
          issues?.push({ field: 'date', message: 'Date is more than 10 years old', severity: 'warning' });
        }
      }
    }
    
    // Validate description
    if (!transaction?.description || transaction?.description?.trim()?.length === 0) {
      issues?.push({ field: 'description', message: 'Description is empty', severity: 'error' });
    } else if (transaction?.description?.trim()?.length < 3) {
      issues?.push({ field: 'description', message: 'Description is too short', severity: 'warning' });
    }
    
    // Validate amount
    if (transaction?.amount === null || transaction?.amount === undefined) {
      issues?.push({ field: 'amount', message: 'Amount is missing', severity: 'error' });
    } else if (transaction?.amount === 0) {
      issues?.push({ field: 'amount', message: 'Amount is zero', severity: 'warning' });
    } else if (transaction?.amount < 0) {
      issues?.push({ field: 'amount', message: 'Amount is negative', severity: 'error' });
    } else if (transaction?.amount > 1000000) {
      issues?.push({ field: 'amount', message: 'Amount is unusually large', severity: 'warning' });
    }
    
    // Validate type
    if (!['income', 'expense', 'transfer']?.includes(transaction?.type)) {
      issues?.push({ field: 'type', message: 'Invalid transaction type', severity: 'error' });
    }
    
    return {
      isValid: issues?.filter(i => i?.severity === 'error')?.length === 0,
      issues,
      hasWarnings: issues?.filter(i => i?.severity === 'warning')?.length > 0
    };
  },

  /**
   * Check for duplicate transactions
   */
  async checkDuplicates(transaction, accountId, existingTransactions) {
    if (!existingTransactions || existingTransactions?.length === 0) {
      return { isDuplicate: false, matches: [] };
    }
    
    const matches = [];
    
    // Check for exact matches (same date, description, amount)
    const exactMatches = existingTransactions?.filter(existing => 
      existing?.accountId === accountId &&
      existing?.date === transaction?.date &&
      existing?.description?.toLowerCase() === transaction?.description?.toLowerCase() &&
      Math.abs(existing?.amount - transaction?.amount) < 0.01
    );
    
    if (exactMatches?.length > 0) {
      matches?.push(...exactMatches?.map(m => ({ ...m, matchType: 'exact' })));
    }
    
    // Check for similar matches (same date and amount, similar description)
    const similarMatches = existingTransactions?.filter(existing => {
      if (exactMatches?.some(em => em?.id === existing?.id)) return false;
      
      return existing?.accountId === accountId &&
        existing?.date === transaction?.date &&
        Math.abs(existing?.amount - transaction?.amount) < 0.01 &&
        this.calculateSimilarity(existing?.description, transaction?.description) > 0.7;
    });
    
    if (similarMatches?.length > 0) {
      matches?.push(...similarMatches?.map(m => ({ ...m, matchType: 'similar' })));
    }
    
    return {
      isDuplicate: matches?.length > 0,
      matches
    };
  },

  /**
   * Calculate string similarity (Levenshtein distance)
   */
  calculateSimilarity(str1, str2) {
    if (!str1 || !str2) return 0;
    
    const s1 = str1?.toLowerCase();
    const s2 = str2?.toLowerCase();
    
    if (s1 === s2) return 1;
    
    const longer = s1?.length > s2?.length ? s1 : s2;
    const shorter = s1?.length > s2?.length ? s2 : s1;
    
    if (longer?.length === 0) return 1;
    
    const editDistance = this.levenshteinDistance(longer, shorter);
    return (longer?.length - editDistance) / longer?.length;
  },

  /**
   * Calculate Levenshtein distance
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];
    
    for (let i = 0; i <= str2?.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1?.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2?.length; i++) {
      for (let j = 1; j <= str1?.length; j++) {
        if (str2?.charAt(i - 1) === str1?.charAt(j - 1)) {
          matrix[i][j] = matrix?.[i - 1]?.[j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix?.[i - 1]?.[j - 1] + 1,
            matrix?.[i]?.[j - 1] + 1,
            matrix?.[i - 1]?.[j] + 1
          );
        }
      }
    }
    
    return matrix?.[str2?.length]?.[str1?.length];
  },

  /**
   * Get validation summary for all transactions
   */
  getValidationSummary(transactions) {
    const summary = {
      total: transactions?.length,
      valid: 0,
      errors: 0,
      warnings: 0,
      duplicates: 0,
      issues: []
    };
    
    transactions?.forEach((transaction, index) => {
      const validation = transaction?.validation || {};
      
      if (validation?.isValid) {
        summary.valid++;
      } else {
        summary.errors++;
      }
      
      if (validation?.hasWarnings) {
        summary.warnings++;
      }
      
      if (transaction?.duplicateCheck?.isDuplicate) {
        summary.duplicates++;
      }
      
      // Collect all issues
      if (validation?.issues && validation?.issues?.length > 0) {
        validation?.issues?.forEach(issue => {
          summary?.issues?.push({
            row: index + 1,
            ...issue
          });
        });
      }
    });
    
    return summary;
  }
};